import { useRef, useState, useCallback } from "react";

/**
 * ImagePositioner — shows an image preview with a draggable crosshair.
 * The focal point is saved as a CSS object-position string, e.g. "35% 60%".
 *
 * Props:
 *   url       {string}  — image src
 *   position  {string}  — CSS value like "50% 50%" (default if omitted)
 *   onChange  {fn}      — called with new position string, e.g. "35% 60%"
 */
export default function ImagePositioner({ url, position, onChange }) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(() => parsePos(position));

  function parsePos(str) {
    if (!str) return { x: 50, y: 50 };
    const parts = str.trim().split(/\s+/);
    const x = parseFloat(parts[0]);
    const y = parseFloat(parts[1]);
    return {
      x: isNaN(x) ? 50 : x,
      y: isNaN(y) ? 50 : y,
    };
  }

  const computePos = useCallback(
    (clientX, clientY) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const x = Math.round(Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)));
      const y = Math.round(Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100)));
      setPos({ x, y });
      onChange?.(`${x}% ${y}%`);
    },
    [onChange],
  );

  // ── Mouse ──────────────────────────────────────────────────────────────────
  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      computePos(e.clientX, e.clientY);
      const onMove = (ev) => computePos(ev.clientX, ev.clientY);
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [computePos],
  );

  // ── Touch ──────────────────────────────────────────────────────────────────
  const onTouchStart = useCallback(
    (e) => {
      e.preventDefault();
      computePos(e.touches[0].clientX, e.touches[0].clientY);
    },
    [computePos],
  );
  const onTouchMove = useCallback(
    (e) => {
      e.preventDefault();
      computePos(e.touches[0].clientX, e.touches[0].clientY);
    },
    [computePos],
  );

  if (!url) return null;

  const objectPos = `${pos.x}% ${pos.y}%`;

  return (
    <div data-testid="image-positioner">
      {/* Section label */}
      <p
        className="ed-label mt-3"
        style={{ marginBottom: 6 }}
      >
        FOCAL POINT
      </p>

      {/* Image container — explicit height so it never collapses */}
      <div
        ref={containerRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        style={{
          position: "relative",
          height: 220,
          width: "100%",
          overflow: "hidden",
          cursor: "crosshair",
          border: "1px solid #3A3A3A",
          userSelect: "none",
          touchAction: "none",
          background: "#0E0E0E",
        }}
      >
        <img
          src={url}
          alt="Focal point preview"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: objectPos,
            display: "block",
            pointerEvents: "none",
          }}
        />

        {/* Horizontal crosshair line */}
        <div
          style={{
            position: "absolute",
            top: `${pos.y}%`,
            left: 0,
            width: "100%",
            height: 1,
            background: "rgba(255,255,255,0.55)",
            pointerEvents: "none",
            transform: "translateY(-50%)",
          }}
        />

        {/* Vertical crosshair line */}
        <div
          style={{
            position: "absolute",
            left: `${pos.x}%`,
            top: 0,
            width: 1,
            height: "100%",
            background: "rgba(255,255,255,0.55)",
            pointerEvents: "none",
            transform: "translateX(-50%)",
          }}
        />

        {/* Centre dot */}
        <div
          style={{
            position: "absolute",
            top: `${pos.y}%`,
            left: `${pos.x}%`,
            width: 14,
            height: 14,
            borderRadius: "50%",
            border: "2px solid white",
            background: "#CC1A1A",
            pointerEvents: "none",
            transform: "translate(-50%, -50%)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.6)",
          }}
        />

        {/* Corner hint */}
        <span
          style={{
            position: "absolute",
            bottom: 8,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.65)",
            color: "#ccc",
            fontFamily: "monospace",
            fontSize: 10,
            letterSpacing: "0.14em",
            padding: "3px 8px",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          DRAG TO REPOSITION
        </span>
      </div>

      {/* Position readout */}
      <p
        style={{
          fontFamily: "monospace",
          fontSize: 10,
          color: "#555",
          marginTop: 4,
          letterSpacing: "0.1em",
        }}
      >
        position: {objectPos}
      </p>
    </div>
  );
}
